import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import { expandApp, notifyAppReady, isTelegramWebApp } from "./lib/telegram-service";
import { initStorageClear, checkForceLogoutParam } from "./lib/storage-clear";
import { initVersionSystem } from "./lib/version-check";
import { getToken } from "./lib/token-storage";

// Safe check for browser environment
const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

// =============================================================================
// ANTI-BLACK SCREEN PROTECTION
// Questo codice rileva se l'app non si avvia correttamente e forza il reload
// =============================================================================

if (isBrowser) {
  // Timeout di sicurezza: se dopo 10 secondi il root è ancora vuoto, forza reload
  const BLACK_SCREEN_TIMEOUT = 10000;
  const startTime = Date.now();
  
  // Marca l'avvio
  (window as any).__APP_START_TIME = startTime;
  (window as any).__APP_LOADED = false;
  
  const checkBlackScreen = setInterval(() => {
    const rootElement = document.getElementById("root");
    const elapsed = Date.now() - startTime;
    
    // Se è passato troppo tempo e il root è vuoto o solo con spazi
    if (elapsed > BLACK_SCREEN_TIMEOUT && rootElement) {
      const content = rootElement.innerHTML.trim();
      const isEmpty = !content || content === '' || content === '<!-- -->';
      
      if (isEmpty && !(window as any).__APP_LOADED) {
        console.error('[BLACK_SCREEN] Detected black screen! Forcing reload...');
        clearInterval(checkBlackScreen);
        
        // Mostra messaggio all'utente
        const msg = document.createElement('div');
        msg.innerHTML = `
          <div style="
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: #1a1a2e;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            color: white;
            font-family: system-ui, sans-serif;
            z-index: 999999;
            padding: 20px;
            text-align: center;
          ">
            <div style="font-size: 48px; margin-bottom: 20px;">🔄</div>
            <h2 style="margin: 0 0 10px 0;">Aggiornamento necessario</h2>
            <p style="opacity: 0.8; max-width: 300px;">
              Stiamo caricando la nuova versione dell'app...
            </p>
          </div>
        `;
        document.body.appendChild(msg);
        
        // Forza reload dopo 1 secondo
        setTimeout(() => {
          const url = new URL(window.location.href);
          url.searchParams.set('_recovery', Date.now().toString());
          window.location.replace(url.toString());
        }, 1000);
      }
    }
  }, 1000);
  
  // Inizializza il sistema di version checking (auto-reload sulle nuove versioni)
  // SOLO per l'app vera (dentro Telegram o utente web loggato). I visitatori
  // web non loggati vedono la landing pubblica: lì l'auto-reload è inutile e
  // fastidioso (ricaricava la pagina da solo), quindi lo saltiamo.
  const isPublicLandingVisitor = !isTelegramWebApp() && !getToken();
  if (!isPublicLandingVisitor) {
    initVersionSystem();
  }
}

// Funzione di inizializzazione dell'applicazione
const initApplication = () => {
  if (!isBrowser) return;
  
  try {
    console.log('[INIT] Avvio inizializzazione applicazione...');
    
    // 1. Controllo parametri URL per logout forzato
    try {
      if (checkForceLogoutParam()) {
        console.log('[INIT] Parametro force_logout rilevato');
      }
      
      // Esegui la pulizia dello storage se necessario
      initStorageClear();
    } catch (error) {
      console.error('[INIT] Errore durante l\'inizializzazione storage:', error);
    }
    
    // 2. Inizializzazione Telegram WebApp
    try {
      if (isTelegramWebApp()) {
        // Informiamo Telegram che l'app è pronta
        notifyAppReady();
        
        // Espandiamo l'app Telegram a tutto schermo
        expandApp();
        
        console.log('[INIT] Telegram Web App inizializzata con successo');
      } else {
        console.log('[INIT] Telegram Web App non disponibile - esecuzione in modalità standalone');
      }
    } catch (telegramError) {
      console.error('[INIT] Errore durante l\'inizializzazione Telegram WebApp:', telegramError);
      // Continue without Telegram - don't crash
    }
    
    console.log('[INIT] Inizializzazione applicazione completata');
  } catch (error) {
    console.error('[INIT] Errore critico durante l\'inizializzazione:', error);
  }
};

// Prevenzione conflitti HTML5 drag and drop con Replit
const preventDragConflicts = () => {
  if (!isBrowser) return;
  
  try {
    // Previeni inizializzazioni multiple di drag and drop backend
    if ((window as any).__html5_dnd_backend_initialized) {
      console.log('[INIT] HTML5 DnD backend già inizializzato, skip');
      return;
    }
    (window as any).__html5_dnd_backend_initialized = true;
    
    // Prevenzione GLOBALE degli errori HTML5 backend
    const originalError = window.onerror;
    window.onerror = function(message, source, lineno, colno, error) {
      if (typeof message === 'string' && 
          (message.includes('Cannot have two HTML5 backends') || 
           message.includes('HTML5 backends at the same time'))) {
        console.warn('[DRAG CONFLICT] HTML5 backend conflict suppressed globally');
        return true;
      }
      if (originalError) {
        return originalError.call(window, message, source, lineno, colno, error);
      }
      return false;
    };
    
    // Override del console.error per gli errori di Replit
    const originalConsoleError = console.error;
    console.error = function(...args) {
      const message = args[0];
      if (typeof message === 'string' && 
          (message.includes('Cannot have two HTML5 backends') || 
           message.includes('HTML5 backends at the same time'))) {
        console.warn('[DRAG CONFLICT] HTML5 backend conflict suppressed');
        return;
      }
      originalConsoleError.apply(console, args);
    };
    
    // Gestione errori non catturati
    window.addEventListener('unhandledrejection', (event) => {
      if (event.reason && typeof event.reason.message === 'string' &&
          (event.reason.message.includes('Cannot have two HTML5 backends') || 
           event.reason.message.includes('HTML5 backends at the same time'))) {
        console.warn('[DRAG CONFLICT] Unhandled rejection suppressed:', event.reason.message);
        event.preventDefault();
      }
    });
    
    // Previeni eventi di drag predefiniti che possono confliggere
    const preventDragEvents = (e: Event) => {
      if (!e.target || !(e.target as Element).closest('.allow-drag')) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    
    // Aggiungi tutti i listener di prevenzione drag (passive per evitare warning)
    ['dragstart', 'dragover', 'dragenter', 'dragleave', 'drop'].forEach(eventType => {
      document.addEventListener(eventType, preventDragEvents, { capture: true, passive: true });
    });
  } catch (error) {
    console.error('[INIT] Errore nella prevenzione conflitti drag:', error);
  }
};

// Main initialization wrapped in try-catch
try {
  // Esegui la prevenzione conflitti PRIMA del rendering (only in browser)
  if (isBrowser) {
    preventDragConflicts();
  }

  // Rendering dell'applicazione
  const rootElement = isBrowser ? document.getElementById("root") : null;
  if (!rootElement) {
    if (isBrowser) {
      console.error('[INIT] Root element not found');
    }
  } else {
    createRoot(rootElement).render(<App />);
    
    // Segnala che l'app si è caricata correttamente
    (window as any).__APP_LOADED = true;
    
    // Esegui l'inizializzazione dopo il rendering del componente
    setTimeout(initApplication, 100);
  }

  // Enable text selection in Replit development mode (only in browser)
  if (isBrowser && import.meta.env.DEV) {
    try {
      if (window.location.hostname.includes('replit.dev') || 
          window.location.hostname.includes('replit.co')) {
        document.body.setAttribute('data-replit-dev', 'true');
        console.log('[REPLIT-DEV] Text selection enabled for development');
      }
    } catch (e) {
      // Ignore errors in dev-only code
    }
  }
} catch (error) {
  console.error('[INIT] Fatal error during app initialization:', error);
}
